import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Brain,
  Calendar,
  FileText,
  GitBranch,
  Loader2,
  RefreshCw,
  Save,
  Send,
  Settings,
  UserCheck,
} from "lucide-react";
import { useEffect, useState } from "react";
import { PageHeader } from "@/components/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { orpc, rpcClient } from "@/lib/rpc";

type Employee = {
  id: string;
  fullName: string;
  corporateEmail: string | null;
  slackId: string | null;
  userId: string | null;
};

type EmployeeWithUser = Employee & {
  user?: { id: string; email: string; isSuperAdmin: boolean } | null;
};

export default function SettingsPage({ embedded }: { embedded?: boolean }) {
  const { toast } = useToast();
  const [thresholdValue, setThresholdValue] = useState<number>(70);
  const [selectedSyncRecipient, setSelectedSyncRecipient] = useState<string>("");
  const [selectedDossierRecipients, setSelectedDossierRecipients] = useState<string[]>([]);
  const [selectedDraftNotifiers, setSelectedDraftNotifiers] = useState<string[]>([]);
  const [changelogApprover, setChangelogApprover] = useState<string>("");
  const [changelogSlackChannel, setChangelogSlackChannel] = useState<string>("");
  const [changelogGithubRepo, setChangelogGithubRepo] = useState<string>("");
  const [changelogGithubToken, setChangelogGithubToken] = useState<string>("");

  const { data: thresholdData, isLoading: loadingThreshold } = useQuery(
    orpc.settings.getAiVettingThreshold.queryOptions(),
  );

  const { data: weeklySyncData, isLoading: loadingWeeklySync } = useQuery(
    orpc.settings.getWeeklySyncNotify.queryOptions(),
  );

  const { data: dossierRecipientsData, isLoading: loadingDossierRecipients } = useQuery(
    orpc.settings.getDossierRecipients.queryOptions(),
  );

  const { data: changelogSettings, isLoading: loadingChangelog } = useQuery(
    orpc.settings.getChangelog.queryOptions(),
  );

  const { data: draftNotifiers, isLoading: loadingDraftNotifiers } = useQuery(
    orpc.settings.getPulseDraftNotifiers.queryOptions(),
  );

  // Who may be notified is computed by the backend (platform admin, Admin de
  // Pesquisas or Operador de Pesquisas) — this list is not filtered here. It
  // also carries anyone already saved who lost the access, flagged with
  // `canOperateSurveys: false`, so the admin can uncheck them.
  const { data: notifierCandidatesData, isLoading: loadingNotifierCandidates } = useQuery(
    orpc.settings.listPulseDraftNotifierCandidates.queryOptions(),
  );
  const notifierCandidates = notifierCandidatesData ?? [];

  const { data: employeesData, isLoading: loadingEmployees } = useQuery<EmployeeWithUser[]>(
    orpc.employees.list.queryOptions(),
  );

  const employees: EmployeeWithUser[] = Array.isArray(employeesData) ? employeesData : [];

  const superAdminEmployees = employees.filter((e) => e.user?.isSuperAdmin);

  const employeesWithSlack = employees.filter((e) => e.slackId);

  useEffect(() => {
    if (thresholdData) {
      setThresholdValue(thresholdData.threshold);
    }
  }, [thresholdData]);

  useEffect(() => {
    if (weeklySyncData) {
      setSelectedSyncRecipient(weeklySyncData.employeeId || "");
    }
  }, [weeklySyncData]);

  useEffect(() => {
    if (dossierRecipientsData) {
      setSelectedDossierRecipients(dossierRecipientsData.map((r) => r.employeeId));
    }
  }, [dossierRecipientsData]);

  useEffect(() => {
    if (draftNotifiers) {
      setSelectedDraftNotifiers(draftNotifiers.map((n) => n.employeeId));
    }
  }, [draftNotifiers]);

  useEffect(() => {
    if (changelogSettings) {
      setChangelogApprover(changelogSettings.approverEmployeeId || "");
      setChangelogSlackChannel(changelogSettings.slackChannel || "");
      setChangelogGithubRepo(changelogSettings.githubRepo || "");
    }
  }, [changelogSettings]);

  const saveThresholdMutation = useMutation({
    mutationFn: async (threshold: number) => {
      await rpcClient.settings.setAiVettingThreshold({ threshold });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(orpc.settings.getAiVettingThreshold.queryOptions());
      toast({ title: "Threshold atualizado com sucesso" });
    },
    onError: () => {
      toast({ title: "Erro ao salvar threshold", variant: "destructive" });
    },
  });

  const saveSyncRecipientMutation = useMutation({
    mutationFn: async (employeeId: string | null) => {
      await rpcClient.settings.setWeeklySyncNotify({ employeeId });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(orpc.settings.getWeeklySyncNotify.queryOptions());
      toast({ title: "Destinatário do sync semanal atualizado com sucesso" });
    },
    onError: () => {
      toast({ title: "Erro ao salvar destinatário", variant: "destructive" });
    },
  });

  const saveDossierRecipientsMutation = useMutation({
    mutationFn: async (employeeIds: string[]) => {
      await rpcClient.settings.setDossierRecipients({ employeeIds });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(orpc.settings.getDossierRecipients.queryOptions());
      toast({ title: "Destinatários do dossiê atualizados com sucesso" });
    },
    onError: () => {
      toast({ title: "Erro ao salvar destinatários do dossiê", variant: "destructive" });
    },
  });

  const saveDraftNotifiersMutation = useMutation({
    mutationFn: async (employeeIds: string[]) => {
      await rpcClient.settings.setPulseDraftNotifiers({ employeeIds });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(orpc.settings.getPulseDraftNotifiers.queryOptions());
      queryClient.invalidateQueries(orpc.settings.listPulseDraftNotifierCandidates.queryOptions());
      toast({ title: "Notificados de pesquisa de clima atualizados com sucesso" });
    },
    onError: (error: Error) => {
      // Surfaces the eligibility message from the server — the admin needs to
      // know WHO is blocking the save, not just that it failed.
      toast({
        title: "Erro ao salvar notificados de pesquisa de clima",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const saveChangelogSettingsMutation = useMutation({
    mutationFn: async (data: {
      approverEmployeeId: string | null;
      slackChannel: string | null;
      githubRepo: string | null;
      githubToken: string | null;
    }) => {
      await rpcClient.settings.setChangelog(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(orpc.settings.getChangelog.queryOptions());
      setChangelogGithubToken("");
      // biome-ignore lint/security/noSecrets: UI toast message, not a secret
      toast({ title: "Configurações de changelog atualizadas com sucesso" });
    },
    onError: () => {
      toast({ title: "Erro ao salvar configurações de changelog", variant: "destructive" });
    },
  });

  const triggerSyncMutation = useMutation({
    mutationFn: async () => {
      return rpcClient.settings.triggerWeeklySync();
    },
    onSuccess: (data: any) => {
      if (data.sent && data.newUsersCount > 0) {
        toast({
          title: `Notificação enviada com ${data.newUsersCount} novo${data.newUsersCount !== 1 ? "s" : ""} colaborador${data.newUsersCount !== 1 ? "es" : ""}`,
        });
      } else if (data.sent && data.newUsersCount === 0) {
        toast({ title: "Nenhum novo colaborador encontrado no Slack" });
      } else {
        toast({ title: data.error || "Falha ao disparar sync", variant: "destructive" });
      }
    },
    onError: () => {
      toast({ title: "Erro ao disparar sync semanal", variant: "destructive" });
    },
  });

  const toggleDossierRecipient = (employeeId: string) => {
    setSelectedDossierRecipients((prev) =>
      prev.includes(employeeId) ? prev.filter((id) => id !== employeeId) : [...prev, employeeId],
    );
  };

  const toggleDraftNotifier = (employeeId: string) => {
    setSelectedDraftNotifiers((prev) =>
      prev.includes(employeeId) ? prev.filter((id) => id !== employeeId) : [...prev, employeeId],
    );
  };

  const thresholdChanged = thresholdData ? thresholdValue !== thresholdData.threshold : false;

  const syncRecipientChanged = weeklySyncData
    ? selectedSyncRecipient !== (weeklySyncData.employeeId || "")
    : false;

  const dossierRecipientsChanged = (() => {
    if (!dossierRecipientsData) return false;
    const currentIds = dossierRecipientsData.map((r) => r.employeeId).sort();
    const selectedIds = [...selectedDossierRecipients].sort();
    if (currentIds.length !== selectedIds.length) return true;
    return currentIds.some((id, i) => id !== selectedIds[i]);
  })();

  const draftNotifiersChanged = (() => {
    if (!draftNotifiers) return false;
    const currentIds = draftNotifiers.map((n) => n.employeeId).sort();
    const selectedIds = [...selectedDraftNotifiers].sort();
    if (currentIds.length !== selectedIds.length) return true;
    return currentIds.some((id, i) => id !== selectedIds[i]);
  })();

  if (
    loadingEmployees ||
    loadingThreshold ||
    loadingWeeklySync ||
    loadingDossierRecipients ||
    loadingChangelog ||
    loadingDraftNotifiers ||
    loadingNotifierCandidates
  ) {
    return (
      <div className="space-y-6" data-testid="loading-settings">
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="rounded-md border border-[hsl(var(--sand-divider))] bg-[hsl(var(--paper-white))] p-6 space-y-3"
          >
            <Skeleton className="h-5 w-48" />
            <Skeleton className="h-3 w-full max-w-xl" />
            <Skeleton className="h-3 w-full max-w-md" />
            <Skeleton className="h-10 w-full" />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {!embedded && (
        <PageHeader
          title="Configurações"
          description="Configurações gerais do sistema"
          icon={<Settings className="h-6 w-6 text-primary" />}
          titleTestId="text-settings-title"
        />
      )}

      <Card data-testid="card-weekly-slack-sync">
        <CardHeader>
          <div className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-[hsl(var(--ink-primary))]" />
            <div>
              <CardTitle>Sync Semanal de Novos Colaboradores</CardTitle>
              <CardDescription>
                Toda segunda-feira, o sistema verifica novos usuários no Slack que não estão
                cadastrados na plataforma e envia uma notificação ao destinatário configurado. A
                pessoa poderá importar diretamente pelo Slack, definindo o líder de cada novo
                colaborador.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="space-y-2">
              <span className="text-sm font-medium">Destinatário da notificação</span>
              <SearchableSelect
                value={selectedSyncRecipient}
                onValueChange={setSelectedSyncRecipient}
                options={employeesWithSlack.map((e) => ({
                  value: e.id,
                  label: `${e.fullName}${e.corporateEmail ? ` (${e.corporateEmail})` : ""}`,
                }))}
                placeholder="Selecione um colaborador com Slack vinculado..."
                searchPlaceholder="Buscar colaborador..."
                emptyMessage="Nenhum colaborador com Slack encontrado"
                data-testid="select-weekly-sync-recipient"
              />
              <p className="text-xs text-muted-foreground">
                Apenas colaboradores com Slack vinculado são listados.
              </p>
            </div>

            <div className="flex items-center justify-between pt-4 border-t">
              <Button
                variant="outline"
                onClick={() => triggerSyncMutation.mutate()}
                disabled={!selectedSyncRecipient || triggerSyncMutation.isPending}
                data-testid="button-trigger-weekly-sync"
              >
                {triggerSyncMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Disparar agora
              </Button>
              <Button
                onClick={() => saveSyncRecipientMutation.mutate(selectedSyncRecipient || null)}
                disabled={!syncRecipientChanged || saveSyncRecipientMutation.isPending}
                data-testid="button-save-weekly-sync"
              >
                {saveSyncRecipientMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Salvar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="card-dossier-recipients">
        <CardHeader>
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-[hsl(var(--ink-primary))]" />
            <div>
              <CardTitle>Destinatários do Dossiê</CardTitle>
              <CardDescription>
                Selecione quais superadmins receberão o dossiê por e-mail quando um candidato chegar
                à etapa de Comitê (Dossiê).
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {superAdminEmployees.length === 0 ? (
            <p className="text-muted-foreground text-sm" data-testid="text-no-superadmins-dossier">
              Nenhum superadmin encontrado com perfil de funcionário vinculado.
            </p>
          ) : (
            <div className="space-y-4">
              <div className="space-y-3">
                {superAdminEmployees.map((emp) => (
                  <label
                    key={emp.id}
                    className="flex items-center gap-3 p-3 rounded-md border border-[hsl(var(--sand-divider))] hover:bg-[hsl(var(--sand-step))] cursor-pointer transition-colors"
                    data-testid={`dossier-recipient-item-${emp.id}`}
                  >
                    <Checkbox
                      checked={selectedDossierRecipients.includes(emp.id)}
                      onCheckedChange={() => toggleDossierRecipient(emp.id)}
                      data-testid={`checkbox-dossier-recipient-${emp.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{emp.fullName}</div>
                      <div className="text-sm text-muted-foreground">
                        {emp.user?.email || emp.corporateEmail || "—"}
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              <div className="flex items-center justify-between gap-2 pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  {selectedDossierRecipients.length} destinatário
                  {selectedDossierRecipients.length !== 1 ? "s" : ""} selecionado
                  {selectedDossierRecipients.length !== 1 ? "s" : ""}
                </p>
                <Button
                  onClick={() => saveDossierRecipientsMutation.mutate(selectedDossierRecipients)}
                  disabled={!dossierRecipientsChanged || saveDossierRecipientsMutation.isPending}
                  data-testid="button-save-dossier-recipients"
                >
                  {saveDossierRecipientsMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Salvar
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="card-pulse-draft-notifiers">
        <CardHeader>
          <div className="flex items-center gap-2">
            <UserCheck className="h-5 w-5 text-[hsl(var(--ink-primary))]" />
            <div>
              <CardTitle>Notificados de Pesquisa de Clima</CardTitle>
              <CardDescription>
                Selecione quem receberá notificação via Slack quando um draft de pesquisa de clima
                for criado automaticamente. Eles terão 7 dias para revisar e editar antes do envio,
                e poderão aprovar ou cancelar o envio pelo Slack. A lista traz quem pode gerir
                pesquisas de clima — admins da plataforma, Admins de Pesquisas e Operadores de
                Pesquisas.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {notifierCandidates.length === 0 ? (
            <p
              className="text-muted-foreground text-sm"
              data-testid="text-no-candidates-draft-notifiers"
            >
              Ninguém com acesso a Pesquisas de Clima e perfil de funcionário vinculado.
            </p>
          ) : (
            <div className="space-y-4">
              <div className="space-y-3">
                {notifierCandidates.map((emp) => (
                  <label
                    key={emp.id}
                    className="flex items-center gap-3 p-3 rounded-md border border-[hsl(var(--sand-divider))] hover:bg-[hsl(var(--sand-step))] cursor-pointer transition-colors"
                    data-testid={`draft-notifier-item-${emp.id}`}
                  >
                    <Checkbox
                      checked={selectedDraftNotifiers.includes(emp.id)}
                      onCheckedChange={() => toggleDraftNotifier(emp.id)}
                      data-testid={`checkbox-draft-notifier-${emp.id}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium">{emp.fullName}</div>
                      <div className="text-sm text-muted-foreground">{emp.email || "\u2014"}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {emp.canOperateSurveys ? null : (
                        <span
                          className="inline-flex items-center text-[13px] font-semibold text-[hsl(var(--alert-red))]"
                          data-testid={`draft-notifier-ineligible-${emp.id}`}
                        >
                          Sem acesso — desmarque
                        </span>
                      )}
                      {emp.slackId ? (
                        <span className="inline-flex items-center text-[13px] font-semibold text-[hsl(var(--forest-pulse))]">
                          Slack vinculado
                        </span>
                      ) : (
                        <span className="inline-flex items-center text-[13px] font-semibold text-[hsl(var(--amber-state))]">
                          Sem Slack
                        </span>
                      )}
                    </div>
                  </label>
                ))}
              </div>

              <div className="flex items-center justify-between pt-4 border-t">
                <p className="text-sm text-muted-foreground">
                  {selectedDraftNotifiers.length} notificado
                  {selectedDraftNotifiers.length !== 1 ? "s" : ""} selecionado
                  {selectedDraftNotifiers.length !== 1 ? "s" : ""}
                </p>
                <Button
                  onClick={() => saveDraftNotifiersMutation.mutate(selectedDraftNotifiers)}
                  disabled={!draftNotifiersChanged || saveDraftNotifiersMutation.isPending}
                  data-testid="button-save-draft-notifiers"
                >
                  {saveDraftNotifiersMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Salvar
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="card-changelog-settings">
        <CardHeader>
          <div className="flex items-center gap-2">
            <GitBranch className="h-5 w-5 text-[hsl(var(--ink-primary))]" />
            <div>
              <CardTitle>Changelog Semanal</CardTitle>
              <CardDescription>
                Configure o repositório GitHub, aprovador e canal do Slack para o changelog semanal
                automático. Toda segunda-feira, o sistema busca commits/PRs e gera um resumo via IA.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="space-y-2">
              <label htmlFor="changelog-github-repo" className="text-sm font-medium">
                Repositório GitHub
              </label>
              <Input
                id="changelog-github-repo"
                value={changelogGithubRepo}
                onChange={(e) => setChangelogGithubRepo(e.target.value)}
                placeholder="owner/repo (ex: empresa/projeto)"
                data-testid="input-changelog-github-repo"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="changelog-github-token" className="text-sm font-medium">
                Token GitHub
              </label>
              <Input
                id="changelog-github-token"
                type="password"
                value={changelogGithubToken}
                onChange={(e) => setChangelogGithubToken(e.target.value)}
                placeholder={
                  changelogSettings?.hasGithubToken ? "••••••••  (já configurado)" : "ghp_..."
                }
                data-testid="input-changelog-github-token"
              />
              <p className="text-xs text-muted-foreground">
                {changelogSettings?.hasGithubToken
                  ? "Token já configurado. Preencha novamente para alterar."
                  : "Token com permissão de leitura no repositório. Ou defina a env var GITHUB_TOKEN."}
              </p>
            </div>

            <div className="space-y-2">
              <span className="text-sm font-medium">Aprovador do Changelog</span>
              <SearchableSelect
                value={changelogApprover}
                onValueChange={setChangelogApprover}
                options={employeesWithSlack.map((e) => ({
                  value: e.id,
                  label: `${e.fullName}${e.corporateEmail ? ` (${e.corporateEmail})` : ""}`,
                }))}
                placeholder="Selecione quem aprovará o changelog..."
                searchPlaceholder="Buscar colaborador..."
                emptyMessage="Nenhum colaborador com Slack encontrado"
                data-testid="select-changelog-approver"
              />
            </div>

            <div className="space-y-2">
              <label htmlFor="changelog-slack-channel" className="text-sm font-medium">
                Canal do Slack para publicação
              </label>
              <Input
                id="changelog-slack-channel"
                value={changelogSlackChannel}
                onChange={(e) => setChangelogSlackChannel(e.target.value)}
                placeholder="ID do canal (ex: C01234ABCDE)"
                data-testid="input-changelog-slack-channel"
              />
              <p className="text-xs text-muted-foreground">
                ID do canal Slack onde o changelog será publicado após aprovação.
              </p>
            </div>

            <div className="flex items-center justify-end pt-4 border-t">
              <Button
                onClick={() =>
                  saveChangelogSettingsMutation.mutate({
                    approverEmployeeId: changelogApprover || null,
                    slackChannel: changelogSlackChannel || null,
                    githubRepo: changelogGithubRepo || null,
                    githubToken: changelogGithubToken || null,
                  })
                }
                disabled={saveChangelogSettingsMutation.isPending}
                data-testid="button-save-changelog-settings"
              >
                {saveChangelogSettingsMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Salvar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card data-testid="card-ai-vetting-threshold">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-[hsl(var(--ink-primary))]" />
            <div>
              <CardTitle>Threshold de Priorização IA</CardTitle>
              <CardDescription>
                Candidatos com nota de vetting IA igual ou acima deste valor serão automaticamente
                movidos para "Priorizado". Abaixo deste valor, serão movidos para "Despriorizado".
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="flex-1 max-w-[200px]">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={thresholdValue}
                  onChange={(e) => setThresholdValue(parseInt(e.target.value, 10) || 0)}
                  className="tabular-nums"
                  data-testid="input-ai-vetting-threshold"
                />
              </div>
              <span className="text-sm text-muted-foreground">de 0 a 100</span>
            </div>

            <div className="flex items-center justify-between pt-4 border-t">
              <p className="text-sm text-muted-foreground tabular-nums">
                Candidatos com nota &ge; {thresholdValue} = Priorizado
              </p>
              <Button
                onClick={() => saveThresholdMutation.mutate(thresholdValue)}
                disabled={!thresholdChanged || saveThresholdMutation.isPending}
                data-testid="button-save-threshold"
              >
                {saveThresholdMutation.isPending ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Salvar
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <CalendarSyncCard />
    </div>
  );
}

type SyncDetail = {
  status: string;
  calendarEmail: string;
  processed: number;
  total_changes: number;
  reason?: string;
};

type SyncResult = {
  success: boolean;
  calendars: number;
  totalProcessed: number;
  totalChanges: number;
  details: SyncDetail[];
};

function CalendarSyncCard() {
  const { toast } = useToast();
  const [lastResult, setLastResult] = useState<SyncResult | null>(null);

  const syncMutation = useMutation({
    mutationFn: async () => {
      return rpcClient.settings.calendarForceSync() as Promise<SyncResult>;
    },
    onSuccess: (data) => {
      setLastResult(data);
      queryClient.invalidateQueries({ queryKey: orpc.interviews.mine.queryOptions().queryKey });
      toast({
        title: "Sincronização concluída",
        description: `${data.totalProcessed} entrevista(s) atualizada(s) em ${data.calendars} calendário(s).`,
      });
    },
    onError: (error: Error) => {
      toast({ title: "Erro na sincronização", description: error.message, variant: "destructive" });
    },
  });

  return (
    <Card data-testid="card-calendar-sync">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-[hsl(var(--ink-primary))]" />
          Sincronização de Calendário
        </CardTitle>
        <CardDescription>
          Força a re-sincronização de todos os calendários dos entrevistadores com o Google
          Calendar. Use quando agendamentos não foram processados automaticamente.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <Button
            onClick={() => syncMutation.mutate()}
            disabled={syncMutation.isPending}
            data-testid="button-force-calendar-sync"
          >
            {syncMutation.isPending ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            {syncMutation.isPending ? "Sincronizando..." : "Forçar Sincronização"}
          </Button>

          {lastResult && (
            <div className="rounded-md border border-[hsl(var(--sand-divider))] bg-[hsl(var(--paper-2))] p-4 space-y-3">
              <div className="flex items-center gap-4 text-sm tabular-nums">
                <span className="inline-flex items-center text-[13px] font-semibold text-[hsl(var(--ink-primary))]">
                  {lastResult.calendars} calendário(s)
                </span>
                <span
                  className={`inline-flex items-center text-[13px] font-semibold ${
                    lastResult.totalProcessed > 0
                      ? "text-[hsl(var(--forest-pulse))]"
                      : "text-[hsl(var(--ink-muted))]"
                  }`}
                >
                  {lastResult.totalProcessed} entrevista(s) atualizada(s)
                </span>
                <span className="text-[hsl(var(--ink-muted))]">
                  {lastResult.totalChanges} evento(s) analisado(s)
                </span>
              </div>
              {lastResult.details.filter((d) => d.processed > 0 || d.status === "error").length >
                0 && (
                <div className="text-xs space-y-1">
                  {lastResult.details
                    .filter((d) => d.processed > 0 || d.status === "error")
                    .map((d) => (
                      <div key={d.calendarEmail} className="flex items-center gap-2">
                        <span
                          className={
                            d.status === "error"
                              ? "text-[hsl(var(--alert-red))]"
                              : "text-[hsl(var(--forest-pulse))]"
                          }
                        >
                          {d.status === "error" ? "✗" : "✓"}
                        </span>
                        <span>{d.calendarEmail}</span>
                        {d.processed > 0 && (
                          <span className="text-[hsl(var(--ink-muted))] tabular-nums">
                            ({d.processed} atualizada(s))
                          </span>
                        )}
                        {d.reason && (
                          <span className="text-[hsl(var(--alert-red))]">({d.reason})</span>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
